import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import { useSearchParams } from 'react-router-dom';
import {
  getClientNotes,
  getUsersImageNotes,
  postCreateNote,
  putUpdateNote
} from 'store/clientProfile/clientProfileActions';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCurrentNoteID } from 'modules/networkTools/localStorageTokens';

import { useNoteFormContext } from './noteFormContext';
import { NoteFormValues } from '../utils/validation';
import { remapValues, removeEmptyFieldsFromObject } from '../utils/helpers';

export type AutosaveContextValue = {
  isAutosaving: boolean;
  isAutosaveQueued: boolean;
  isAutosaveBusy: boolean;
  lastSaved: string | null;
  enabled: boolean;
  clearLastSaved: () => void;
};

const AutosaveContext = createContext<AutosaveContextValue | null>(null);

export const useAutosaveContext = () => {
  const context = useContext(AutosaveContext);
  // Return a default value if not within provider (autosave disabled)
  if (!context) {
    return {
      isAutosaving: false,
      isAutosaveQueued: false,
      isAutosaveBusy: false,
      lastSaved: null,
      enabled: false,
      clearLastSaved: () => {}
    };
  }
  return context;
};

// Hook to detect first render
function useIsFirstRender(): boolean {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;
    return true;
  }

  return isFirst.current;
}

type AutosaveProviderProps = {
  children: ReactNode;
  debounceMs?: number;
  state?: {
    isAutosaving: boolean;
    isAutosaveQueued: boolean;
    lastSaved: string | null;
  };
  actions?: {
    setIsAutosaving: (isAutosaving: boolean) => void;
    setIsAutosaveQueued: (isAutosaveQueued: boolean) => void;
    setLastSaved: (lastSaved: string | null) => void;
    clearLastSaved: () => void;
  };
};

export const AutosaveProvider = ({
  children,
  debounceMs = 1000,
  state: autosaveState,
  actions: autosaveActions
}: AutosaveProviderProps) => {
  const [localIsAutosaving, setLocalIsAutosaving] = useState(false);
  const [localIsAutosaveQueued, setLocalIsAutosaveQueued] = useState(false);
  const [localLastSaved, setLocalLastSaved] = useState<string | null>(null);
  const isAutosaving = autosaveState?.isAutosaving ?? localIsAutosaving;
  const isAutosaveQueued = autosaveState?.isAutosaveQueued ?? localIsAutosaveQueued;
  const lastSaved = autosaveState?.lastSaved ?? localLastSaved;
  const setIsAutosaving = autosaveActions?.setIsAutosaving ?? setLocalIsAutosaving;
  const setIsAutosaveQueued = autosaveActions?.setIsAutosaveQueued ?? setLocalIsAutosaveQueued;
  const setLastSaved = autosaveActions?.setLastSaved ?? setLocalLastSaved;
  const dispatch = useAppDispatch();
  const { clientId, note } = useNoteFormContext();
  const formik = useFormikContext<NoteFormValues>();
  const noteId = note?.id?.toString() ?? formik.values.id?.toString();
  const { size } = useAppSelector((state) => state.clientProfile.clientProfileList);
  const [searchParams] = useSearchParams();
  const noteTypes = searchParams.get('noteTypes');
  const isFirstRender = useIsFirstRender();

  const clearLastSaved = useCallback(() => {
    if (autosaveActions?.clearLastSaved) {
      autosaveActions.clearLastSaved();
    } else {
      setLocalLastSaved(null);
    }
  }, [autosaveActions]);

  const debouncedAutoSave = useRef(
    debounce(
      async (
        formikValues: NoteFormValues,
        id: string | undefined,
        types: string | null,
        persistCreatedId: (id: number) => void
      ) => {
        if (formikValues === null) return;
        setIsAutosaveQueued(false);
        setIsAutosaving(true);
        formik.validateForm();

        if (id) {
          const dataUpdate = remapValues({ values: formikValues, clientId, id, draft: true });
          const res = await dispatch(putUpdateNote(removeEmptyFieldsFromObject(dataUpdate) as any));

          if (res.meta.requestStatus === 'fulfilled') {
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });

            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
          }
        } else {
          const dataCreate = remapValues({ values: formikValues, clientId, draft: true });
          const res = await dispatch(
            postCreateNote(removeEmptyFieldsFromObject(dataCreate) as any)
          );

          if (res.meta.requestStatus === 'fulfilled') {
            const createdNoteId = Number(res.payload?.id);
            if (!Number.isNaN(createdNoteId)) {
              setCurrentNoteID({ currentNoteID: createdNoteId, clientId });
              persistCreatedId(createdNoteId);
            }
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            channel.postMessage({ message: 'EDIT_NOTE', data: dataCreate });
            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
            await dispatch(
              getClientNotes({
                clientId,
                page: 0,
                size,
                types: types ?? undefined,
                firstRender: true
              })
            ).then((result: any) => {
              if (result.payload.data.content.length > 0) {
                result.payload.data.content.map((noteItem: ClientProfileContentItem) => {
                  if (noteItem.author.profileImageKey) {
                    dispatch(
                      getUsersImageNotes({
                        profileImageKey: noteItem.author.profileImageKey,
                        userId: clientId,
                        noteId: noteItem.id.toString(),
                        notesList: true
                      })
                    );
                  }
                });
              }
            });
          }
        }
        setIsAutosaving(false);
      },
      debounceMs
    )
  ).current;

  // Watch for form value changes and trigger autosave
  useEffect(() => {
    if (isFirstRender) return;

    const disableAutoSave = !Boolean(formik.values.note) || formik.isSubmitting;
    if (disableAutoSave) return;

    setIsAutosaveQueued(true);
    debouncedAutoSave(formik.values, noteId, noteTypes, (createdNoteId) => {
      formik.setFieldValue('id', createdNoteId, false);
    });
  }, [formik.values]);

  // Prevent page unload while autosaving
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAutosaving) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAutosaving]);

  const contextValue = useMemo(
    () => ({
      isAutosaving,
      isAutosaveQueued,
      isAutosaveBusy: isAutosaving || isAutosaveQueued,
      lastSaved,
      enabled: true,
      clearLastSaved
    }),
    [isAutosaving, isAutosaveQueued, lastSaved, clearLastSaved]
  );

  return <AutosaveContext.Provider value={contextValue}>{children}</AutosaveContext.Provider>;
};
